import { queryOptions, type QueryClient } from '@tanstack/react-query';

import type { PracticeContactViewModel } from 'chaika-types';

import { PracticeApi } from '@api/PracticeApi';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';

export const userPracticeKeys = {
    all: ['userPractices'] as const,
    lists: () => [...userPracticeKeys.all, 'list'] as const,
    byUserId: (userId: string) => [...userPracticeKeys.lists(), { userId }] as const,
    byUserIds: (userIds: string[]) => [...userPracticeKeys.lists(), { userIds: [...userIds].sort() }] as const,
    doctorsByUser: (userId: string) => [...userPracticeKeys.lists(), { userId, doctors: true }] as const,
};

export const userPracticeQueryOptions = {
    byUserId: (userId: string) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: userPracticeKeys.byUserId(userId),
            queryFn: () =>
                PracticeApi.getUserPracticeData(userId, {
                    includeAssistant: true,
                    withoutPracticeHead: false,
                    includeNotReal: true,
                }),
        }),
    byUserIds: (userIds: string[]) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: userPracticeKeys.byUserIds(userIds),
            queryFn: async (): Promise<Record<string, PracticeContactViewModel[]>> => {
                const results = await Promise.all(
                    getUniqChunkedIds([...userIds]).map(ids =>
                        PracticeApi.getUsersPracticeData(ids, {
                            includeAssistant: true,
                            withoutPracticeHead: false,
                            includeNotReal: true,
                        }),
                    ),
                );

                return Object.fromEntries(results.flatMap(record => Object.entries(record)));
            },
        }),
    doctorsByUser: (userId: string) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: userPracticeKeys.doctorsByUser(userId),
            queryFn: () => PracticeApi.getUserDoctorList(userId),
        }),
};

export function invalidateUserPractice(queryClient: QueryClient, userId: string): Promise<void> {
    return queryClient.invalidateQueries({
        queryKey: userPracticeKeys.lists(),
        predicate: ({ queryKey }) => {
            const filters = queryKey[queryKey.length - 1] as { userId?: string; userIds?: string[] };
            return filters?.userId === userId || (filters?.userIds?.includes(userId) ?? false);
        },
    });
}
